import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from 'vue';

// Components
import flatPickr from 'vue-flatpickr-component';

// Helpers
import { add } from '@white-label-helper/date-utilities';
import { getFlatpickrLocale } from 'ecommerce/shared-helpers';
import { v4 as uuidv4 } from 'uuid';

// Types
import type { BaseOptions } from 'flatpickr/dist/types/options';
import type { Instance } from 'flatpickr/dist/types/instance';
type DateRange = (Date[] | string[]) | (Date | string)[];


export default /*#__PURE__*/_defineComponent({
  __name: 'datepicker-range-inline',
  props: {
    startDate: { default: '' },
    endDate: { default: '' },
    minDate: { default: () => new Date() },
    maxDate: { default: () => add(new Date(), { years: 1 }) },
    headerLabel: { default: '' }
  },
  emits: ["on-change", "close", "focus-save-dates"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'on-change', value: DateRange): void;
  (e: 'close'): void;
  (e: 'focus-save-dates'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { startDate: Date | string, endDate: Date | string, minDate: Date, maxDate: Date, headerLabel: string };

/**
 * This component is based on `datepicker.vue`, but is designed to be used inline
 * rather than as a popup, and is configured for date range selection.
 */
const proxy = getCurrentInstance()?.proxy as Vue;
const flatpickrElement = computed<(Instance | null)>(() => proxy.$refs['flatpickr-element']?.fp);
const uniqueFlatpickrId = ref<string>(`flatpickr-${uuidv4()}`);





const previousMonth = ref<number>(-1);

const datesAreEqual = (date1: Date | string, date2: Date | string) => {
  if (date1 instanceof Date && date2 instanceof Date) {
    return date1.getTime() === date2.getTime();
  }
  return false;
}

const getDays = () => {
  const allDays = Array.from(
    document.querySelectorAll(
      '.flatpickr-day:not(.nextMonthDay):not(.prevMonthDay):not(.flatpickr-disabled)'
    )
  ) as HTMLElement[];
  const firstDay = allDays[0] ?? null as HTMLElement | null;
  const lastDay = allDays[allDays.length - 1] ?? null as HTMLElement | null;
  return {
    allDays,
    firstDay,
    lastDay,
  };
}

const listeners = ref<string[]>([]);

/**
 * Register keyboard listeners for accessibility and navigation.
 * - Escape to exit the calendar focus back to the input or save dates.
 * - Arrow keys
 *   - Up/Left on first day to go to the previous month
 *   - Down/Right on last day to go to the next month
 * - Tab keys to cycle tabbable elements per UX requirements
 */
const registerKeyboardListeners = (): void => {
  const { firstDay, lastDay } = getDays();

  const addEventListener = (element: HTMLElement, key: string, callback: (e: KeyboardEvent) => void): void => {
    if (element && !listeners.value.includes(key)) {
      element.addEventListener('keydown', callback);
      listeners.value.push(key);
    }
  };

  const container = document.querySelector(`#${uniqueFlatpickrId.value} .flatpickr-container`) as HTMLElement | null;
  const flatpickrInputElement = document.querySelector(`#${uniqueFlatpickrId.value} .flatpickr-input`) as HTMLInputElement | null;
  const innerContainer = document.querySelector(`#${uniqueFlatpickrId.value} .flatpickr-innerContainer`) as HTMLElement | null;
  const backButton = document.querySelector(`#${uniqueFlatpickrId.value} [data-testid="datepicker-range-inline__header__back"]`) as HTMLElement | null;
  const nextMonthButton = document.querySelector(`#${uniqueFlatpickrId.value} .flatpickr-next-month:not(.flatpickr-disabled)`) as HTMLElement | null;
  const prevMonthButton = document.querySelector(`#${uniqueFlatpickrId.value} .flatpickr-prev-month:not(.flatpickr-disabled)`) as HTMLElement | null;

  if (flatpickrInputElement) {
    flatpickrInputElement.tabIndex = -1;
  }

  const tabbableElements = [
    backButton,
    prevMonthButton,
    nextMonthButton,
    firstDay,
  ].filter((el: HTMLElement | null) => el !== null) as HTMLElement[];

  tabbableElements.forEach((el: HTMLElement, index: number) => {
    el.tabIndex = 0;
    el.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (!event.shiftKey && tabbableElements[index + 1]) {
          event.preventDefault();
          tabbableElements[index + 1]?.focus();
        } else if (!event.shiftKey && !tabbableElements[index + 1]) {
          if (props.startDate && props.endDate) {
            event.preventDefault();
            emit('focus-save-dates');
          }
        } else if (event.shiftKey && tabbableElements[index - 1]) {
          event.preventDefault();
          tabbableElements[index - 1]?.focus();
        }
      }
    });
  });

  if (nextMonthButton) {
    nextMonthButton.tabIndex = 0;
    addEventListener(nextMonthButton, 'nextMonthButton', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Space') {
        event.preventDefault();
        flatpickrElement.value?.changeMonth(1);
        nextMonthButton.focus();
      }
    });
  }

  if (prevMonthButton) {
    prevMonthButton.tabIndex = 0;
    addEventListener(prevMonthButton, 'prevMonthButton', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Space') {
        event.preventDefault();
        flatpickrElement.value?.changeMonth(-1);
        prevMonthButton.focus();
      }
    });
  }

  if (container) {
    container.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (props.startDate && props.endDate) {
          event.preventDefault();
          emit('focus-save-dates');
        } else {
          innerContainer?.focus();
        }
      }
    });
  }

  if (innerContainer) {
    innerContainer.tabIndex = 0;
    innerContainer.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Space') {
        event.preventDefault();
        firstDay?.focus();
      }
    });
  }

  if (firstDay) {
    firstDay.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        flatpickrElement.value?.changeMonth(-1);
      }
    });
  }
  if (lastDay) {
    lastDay.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        flatpickrElement.value?.changeMonth(1);
      }
    });
  }
}

const flatpickrConfig = computed((): Partial<BaseOptions> => {
  return {
    showMonths: 1,
    inline: true,
    static: true,
    minDate: (() => {
      // If we have a start date, block off dates before it.
      if (props.startDate && !props.endDate) {
        return props.startDate;
      }
      // Otherwise use min date prop
      return props.minDate;
    })(),
    maxDate: props.maxDate,
    monthSelectorType: 'static',
    mode: !props.endDate ? 'multiple' : 'range',
    defaultDate: [props.startDate, props.endDate],
    locale: {
      ...getFlatpickrLocale(proxy.$i18n.locale) as object,
      firstDayOfWeek: 1, // Monday
    },
    onReady: (...args: any): void => {
      const { currentMonth } = args[2];
      previousMonth.value = currentMonth;
      registerKeyboardListeners();
    },
    onMonthChange: (...args: any): void => {
      const { currentMonth } = args[2];
      const { firstDay, lastDay } = getDays();

      if (currentMonth > previousMonth.value) {
        // User navigated to the next month
        if (firstDay) firstDay.focus();
      } else if (currentMonth < previousMonth.value) {
        // User navigated to the previous month
        if (lastDay) lastDay.focus();
      }

      registerKeyboardListeners();
      previousMonth.value = currentMonth;
    },
  };
});

const onClickBack = (): void => {
  if (!props.startDate) {
    emit('close');
  } else if (props.startDate && !props.endDate) {
    flatpickrElement.value?.clear();
  } else {
    const date1Copy = new Date(props.startDate);
    flatpickrElement.value?.setDate(date1Copy);
    onChange([date1Copy, '']);
  }
};

const onChange = async (dates: DateRange, ...args: any) => {
  const { latestSelectedDateObj } = args[1] ?? {};

  // If no end date set yet and start date was clicked again - one day trip selected
  if (!props.endDate && datesAreEqual(props.startDate, latestSelectedDateObj)) {
    // Set start and end to same date
    const date1Copy = new Date(props.startDate);
    flatpickrElement.value?.setDate([date1Copy, date1Copy]);
    emit('on-change', [date1Copy, date1Copy]);
  } else if (
    // If one day trip selected, and start date was clicked again
    datesAreEqual(props.startDate, props.endDate) &&
    datesAreEqual(props.startDate, latestSelectedDateObj)
  ) {
    // Clear selected dates
    emit('on-change', []);
    flatpickrElement.value?.clear();
  } else {
    emit('on-change', dates);
  }

  // Set keyboard listeners again after calendar redraw
  await new Promise(resolve => setTimeout(resolve, 10)); // Ensure DOM update
  registerKeyboardListeners();

  // Re-focus on selected date after selection
  const selectedDates = Array.from(
    document.querySelectorAll(
      '.flatpickr-day.selected'
    )
  ) as HTMLElement[];
  if (selectedDates.length) {
    selectedDates[selectedDates.length - 1].focus();
  }
};

return { __sfc: true,proxy, flatpickrElement, uniqueFlatpickrId, props, emit, previousMonth, datesAreEqual, getDays, listeners, registerKeyboardListeners, flatpickrConfig, onClickBack, onChange, flatPickr }
}

})